import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, MapPin, Car, KeyRound, ArrowUpRight, X, CalendarPlus, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "../lib/api";
import { TID } from "../lib/testIds";
import { useConfirm } from "./BackOffice/_shared";
import RentalTimeline from "../components/RentalTimeline";

const STATUS_META = {
  pending_payment: { label: "Pending payment", cls: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30" },
  confirmed: { label: "Confirmed", cls: "bg-gold/10 text-gold border-gold/30" },
  cancelled: { label: "Cancelled", cls: "bg-white/5 text-white/40 border-white/10" },
  completed: { label: "Completed", cls: "bg-electric/10 text-electric border-electric/30" },
};

export default function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extendFor, setExtendFor] = useState(null);
  const [timelineFor, setTimelineFor] = useState(null);
  const { confirm, dialog: confirmDialog } = useConfirm();

  const load = () => {
    setLoading(true);
    api.get("/bookings").then((r) => setBookings(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const pay = async (b) => {
    try {
      const { data } = await api.post("/payments/checkout", {
        booking_id: b.id, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment failed");
    }
  };
  const cancel = async (b) => {
    const ok = await confirm({
      title: "Cancel this booking?",
      message: "Your trip will be released. Deposits already paid are refundable per our cancellation policy.",
      meta: `${b.vehicle_name || "Booking"} · ${b.id}`,
      confirmLabel: "Cancel booking",
      cancelLabel: "Keep booking",
      tone: "warning",
    });
    if (!ok) return;
    try {
      await api.post(`/bookings/${b.id}/cancel`);
      toast.success("Booking cancelled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    }
  };

  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 py-12">
      <div className="label-caps text-gold mb-2">Itinerary</div>
      <h1 className="font-serif text-4xl sm:text-5xl mb-10">Your bookings</h1>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((k) => <div key={k} className="h-40 rounded-2xl bg-ink-800 animate-pulse" />)}
        </div>
      ) : bookings.length === 0 ? (
        <div className="glass rounded-3xl p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-ink-700 mx-auto flex items-center justify-center mb-4">
            <Car className="text-gold" />
          </div>
          <h3 className="font-serif text-2xl mb-2">Nothing booked yet</h3>
          <p className="text-white/60 mb-6">Your next trip starts with the perfect vehicle.</p>
          <Link to="/fleet" className="btn-gold px-6 py-3 rounded-full text-sm inline-flex items-center gap-1">
            Browse fleet <ArrowUpRight size={14} />
          </Link>
        </div>
      ) : (
        <div data-testid={TID.bookings.list} className="space-y-4">
          {bookings.map((b) => {
            const meta = STATUS_META[b.status] || STATUS_META.confirmed;
            return (
              <div
                key={b.id}
                data-testid={TID.bookings.card(b.id)}
                className="glass rounded-2xl p-6 flex flex-col md:flex-row md:items-center gap-6"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    {b.type === "ride"
                      ? <span className="label-caps px-2.5 py-1 rounded-full bg-gold/10 border border-gold/30 text-gold">Chauffeur</span>
                      : <span className="label-caps px-2.5 py-1 rounded-full bg-electric/10 border border-electric/30 text-electric">Rental</span>}
                    <span className={`label-caps px-2.5 py-1 rounded-full border ${meta.cls}`}>{meta.label}</span>
                  </div>
                  <h3 className="font-serif text-2xl mb-2">{b.vehicle_name}</h3>
                  <div className="text-sm text-white/60 space-y-1">
                    {b.type === "ride" ? (
                      <>
                        <div className="flex items-center gap-2"><MapPin size={14} className="text-gold" /> {b.pickup} → {b.dropoff}</div>
                        <div className="flex items-center gap-2"><Calendar size={14} className="text-gold" /> {formatDT(b.pickup_datetime)}</div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2"><KeyRound size={14} className="text-electric" /> {b.pickup_location}</div>
                        <div className="flex items-center gap-2"><Calendar size={14} className="text-electric" /> {formatDT(b.start_at)} — {formatDT(b.end_at)}</div>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 md:min-w-[180px]">
                  <div className="text-2xl font-serif text-gold">${b.amount?.toFixed(2)}</div>
                  <div className="flex gap-2 flex-wrap justify-end">
                    <Link
                      to={`/bookings/${b.id}`}
                      data-testid={TID.bookings.detail(b.id)}
                      className="px-3 py-1.5 rounded-full text-xs border border-white/10 hover:border-white/30"
                    >
                      Details
                    </Link>
                    {b.status === "pending_payment" && (
                      <button
                        data-testid={TID.bookings.payBtn(b.id)}
                        onClick={() => pay(b)}
                        className="btn-gold px-3 py-1.5 rounded-full text-xs"
                      >
                        Pay now
                      </button>
                    )}
                    {b.status !== "cancelled" && b.status !== "completed" && (
                      <button
                        data-testid={TID.bookings.cancelBtn(b.id)}
                        onClick={() => cancel(b)}
                        className="px-3 py-1.5 rounded-full text-xs text-white/60 hover:text-red-400 border border-white/10"
                      >
                        <X size={12} className="inline" /> Cancel
                      </button>
                    )}
                    {b.type === "rental" && (b.status === "confirmed" || b.status === "in_progress") && (
                      <button
                        data-testid={`booking-extend-${b.id}`}
                        onClick={() => setExtendFor(b)}
                        className="px-3 py-1.5 rounded-full text-xs text-cyan hover:text-white border border-cyan/40 inline-flex items-center gap-1"
                      >
                        <CalendarPlus size={12} /> Extend
                      </button>
                    )}
                    <button
                      data-testid={`booking-timeline-${b.id}`}
                      onClick={() => setTimelineFor(timelineFor === b.id ? null : b.id)}
                      className="px-3 py-1.5 rounded-full text-xs text-white/60 hover:text-white border border-white/10 inline-flex items-center gap-1"
                    >
                      {timelineFor === b.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />} Timeline
                    </button>
                  </div>
                  {timelineFor === b.id && (
                    <div className="mt-3">
                      <RentalTimeline bookingId={b.id} />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {confirmDialog}
      {extendFor && (
        <ExtensionModal
          booking={extendFor}
          onClose={() => setExtendFor(null)}
          onDone={() => { setExtendFor(null); load(); }}
        />
      )}
    </section>
  );
}

function ExtensionModal({ booking, onClose, onDone }) {
  const currentEnd = booking.end_at ? booking.end_at.slice(0, 16) : "";
  const [newEnd, setNewEnd] = useState(currentEnd);
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const getQuote = async () => {
    if (!newEnd || newEnd <= currentEnd) {
      toast.error("Pick a return date after your current one");
      return;
    }
    setQuoting(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/extension/quote`, {
        new_end_at: new Date(newEnd).toISOString(),
      });
      setQuote(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't get quote");
    } finally {
      setQuoting(false);
    }
  };

  const submit = async () => {
    if (!quote) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/extension/request`, {
        new_end_at: new Date(newEnd).toISOString(),
      });
      toast.success(
        data.status === "auto_approved"
          ? "Extension approved — your rental now runs to the new date"
          : "Extension request sent — a manager will confirm shortly"
      );
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit extension");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
      data-testid="extension-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-ink-900 border border-white/10 rounded-3xl w-full sm:max-w-lg p-6 space-y-4"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="label-caps text-cyan text-[10px]">Extend rental</div>
            <div className="font-serif text-xl text-white">{booking.vehicle_name}</div>
            <div className="text-white/50 text-xs">
              Currently ends {new Date(booking.end_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
            </div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white text-xs">Close</button>
        </div>
        <label className="block">
          <div className="label-caps text-[9px] text-white/50 mb-1">New return date &amp; time</div>
          <input
            type="datetime-local"
            data-testid="extension-new-end"
            value={newEnd}
            onChange={(e) => { setNewEnd(e.target.value); setQuote(null); }}
            min={currentEnd}
            className="w-full bg-ink-800 border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-cyan"
          />
        </label>
        {!quote && (
          <button
            type="button"
            onClick={getQuote}
            disabled={quoting || !newEnd}
            data-testid="extension-quote-btn"
            className="w-full px-5 py-3 rounded-full bg-cyan/20 text-cyan ring-1 ring-cyan/50 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {quoting ? "Checking availability…" : "Check availability & price"}
          </button>
        )}
        {quote && (
          <div
            data-testid="extension-quote"
            className={`rounded-2xl border p-4 space-y-2 ${
              quote.available ? "border-lime-400/40 bg-lime-400/[0.06]" : "border-red-500/40 bg-red-500/[0.06]"
            }`}
          >
            <div className={`label-caps text-[10px] ${quote.available ? "text-lime-300" : "text-red-300"}`}>
              {quote.available ? "Available" : "Not available"}
            </div>
            {quote.available ? (
              <>
                <div className="text-white text-sm">+{quote.extra_days} day{quote.extra_days > 1 ? "s" : ""}</div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-white/60">Extra subtotal</span>
                  <span className="text-white">${quote.extra_subtotal.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-white/60">Tax</span>
                  <span className="text-white">${quote.extra_tax.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between text-base font-semibold pt-2 border-t border-white/8">
                  <span className="text-white/80">Extra total</span>
                  <span className="text-lime-300">${quote.extra_total.toFixed(2)}</span>
                </div>
                <button
                  type="button"
                  onClick={submit}
                  disabled={submitting}
                  data-testid="extension-submit-btn"
                  className="w-full mt-2 px-5 py-3 rounded-full bg-lime-400 text-black text-sm font-semibold disabled:opacity-50"
                >
                  {submitting ? "Submitting…" : "Confirm extension"}
                </button>
              </>
            ) : (
              <div className="text-red-200 text-sm">{quote.conflict_reason || "Vehicle not available"}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const formatDT = (iso) => {
  if (!iso) return "-";
  return new Date(iso).toLocaleString(undefined, {
    weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
};
